import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, ChevronDown, MapPin, Box, Trash2, Building2, Loader2, Send, Database, AlertTriangle, Gauge } from 'lucide-react';
import { useDmpLookup } from '@/components/dmp/DmpLookup';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { normalizeBusNumber, computeCapacity, capacityNeedsPosition, capacityNeedsCocon } from '@uniticket/shared';
import type {
  BuildingBoxInfo, BuildingAddressVariant, MduApartmentDto, AddBoxesRequest, UnitPosition, CapacityUnit, CapacityResult,
} from '@uniticket/shared';
import { CreateAddressForm } from './CreateAddressForm';
import { capacityUnitsFromApartments, facadeInputFor, CapacitySummary } from './CapacityPanel';
import { fetchBuildingBoxes, addBoxes } from '@/hooks/useAddressCreate';
import { useFacadeCocon, type FacadeCoconState } from '@/hooks/useAddressValidation';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { useAuth } from '@/hooks/useAuth';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PstBisDialog, pstBisOffered } from './PstBisDialog';

// Both modes write the LOCAL copy of public.address (the server refuses unless ADDR_TABLE_TARGET=local, so
// prod/DMP stays read-only). "Create an address" registers a new address; "Add a box" adds one or more units
// (bus_numbers) to an existing building, suggesting the next numbers from the building's existing pattern.

export interface AddressMgmtContext {
  streetName?: string | null;
  postalCode?: string | null;
  municipality?: string | null;
  city?: string | null;
  belmapBuildingId?: string | number | null;
  jvid?: number | null;
  /** The open ticket's id — lets the Create form pull the address enrichment (e.g. its PoP) to pre-fill. */
  ticketId?: string | null;
  /** The open ticket's number (UT-…) — stored in the JVID registry so each minted JVID keeps its source. */
  ticketNumber?: string | null;
  /** N29.1 — « Build a répondu sur ce ticket » (durable). Non nul = les formulaires posent « Build a-t-il confirmé
   *  cette adresse ? » avant d'écrire. Voir `pstBisOffered`. */
  buildRepliedAt?: string | null;
  /** N29.4 (30-09) — « Non » à cette question, sur un ticket : les formulaires ne créent RIEN et appellent ceci ;
   *  la page prépare le refus sans l'envoyer. Posé par la seule fenêtre de validation (absent : console, file MDU). */
  onBuildDeclined?: () => void;
  /** …et ce que ce refus sera, pour que la question l'annonce : le verdict « Rejeté » (ticket lié) ou, sans JVID
   *  (pas de verdict possible), le message à l'opérateur. */
  buildDeclineMode?: 'verdict' | 'message';
  // ── Pre-fill hints used by the "MDU not in Appeee" flow (Cas 1/3) — all still editable in the form. ──
  /** House number to pre-fill the Create form with (Cas 3, from the request's parsed hnr_full). */
  houseNumber?: string | null;
  /** House-number extension to pre-fill the Create form with (Cas 3). */
  houseNumberExtension?: string | null;
  /** @deprecated Le type de logement n'est PAS choisissable : il est dérivé du numéro de boîte côté
   *  serveur (`dwellingTypeForBus`). Ce champ n'a jamais été lu par personne ; passez `busNumber`. */
  dwellingUnitType?: 'SDU' | 'MDU' | null;
  /** Numéro de boîte à pré-remplir dans le formulaire de création (Cas 3 : la demande porte `box_nmb`).
   *  C'est LUI qui décide du type de logement à la création — d'où son remplacement du champ ci-dessus. */
  busNumber?: string | null;
  /** Default the "in deployment scope" toggle (Create), and put an added LU in scope (Add-a-box, Cas 1). */
  inScope?: boolean;
  /** Opened from the DMP console page, anchored on a JVID and NOT on a ticket: the forms then post to
   *  the ADMIN-only console routes. Never set from a ticket or from the MDU queue. */
  viaConsole?: boolean;
  /** Les appartements du bâtiment, pour l'avertissement de capacité à l'ajout d'une boîte. Passés
   *  DEPUIS la page (qui les a déjà) plutôt que rechargés ici : c'est ce qui garantit que la modale et
   *  les pastilles de la fenêtre de validation montrent le MÊME nombre. Absent : la console DMP calcule
   *  alors sur les boîtes de l'adresse choisie (C0.5), et la file MDU n'affiche pas de capacité.
   *  On passe les appartements BRUTS et non des `CapacityUnit` déjà converties, parce qu'il faut
   *  pouvoir les filtrer sur l'adresse choisie — et le seul lien fiable est le jvid. */
  capacityApartments?: MduApartmentDto[];
  /** La lecture COCON de la page (C0.1) — la MÊME que celle des pastilles, pour la même raison que
   *  `capacityApartments` : un seul nombre entre la pastille et le blocage. */
  capacityCocon?: FacadeCoconState;
}

// No dedicated SDU→MDU conversion mode (user decision 2026-07-31): adding a box to an SDU already
// auto-flips it to MDU server-side (logged, reversible) — the explicit form was redundant.
type Mode = 'create' | 'addBox';

export function AddressManagementMenu({ context, onMutated }: { context?: AddressMgmtContext; onMutated?: () => void }) {
  const { t } = useTranslation('tickets');
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState<Mode | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { open: openDmpConsole } = useDmpLookup();

  // Close the dropdown on outside-click (no dropdown primitive in the UI kit — keep it dep-free).
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const open = (m: Mode) => { setMode(m); setMenuOpen(false); };
  // N29.4 : « Non, refuser le ticket » ferme d'abord le volet, puis laisse la page préparer le refus.
  const formContext: AddressMgmtContext | undefined = context?.onBuildDeclined
    ? { ...context, onBuildDeclined: () => { setMode(null); context.onBuildDeclined?.(); } }
    : context;

  return (
    <div className="relative" ref={wrapRef}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setMenuOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <Plus className="w-4 h-4 mr-1" />
        {t('addressMgmt.button')}
        <ChevronDown className={cn('w-3.5 h-3.5 ml-1 transition-transform', menuOpen && 'rotate-180')} />
      </Button>

      {menuOpen && (
        <div
          role="menu"
          // z-40: above the lifted glass cards of the validation window (the ticket context strip is
          // z-10 — its own backdrop-filter stacking context) and below the z-50 modals/sheets.
          className="absolute right-0 mt-1 w-60 rounded-xl border border-slate-200 bg-white shadow-lg z-40 p-1"
        >
          <MenuItem icon={MapPin} label={t('addressMgmt.createAddress')} onClick={() => open('create')} />
          <MenuItem icon={Box} label={t('addressMgmt.addBox')} onClick={() => open('addBox')} />
          <div className="my-1 h-px bg-slate-100" />
          {/* Read-only DMP console (user ask 2026-07-31): preloaded on the ticket's jvid; a no-address
              ticket opens it with the street search pre-filled from the requested address instead. */}
          <MenuItem
            icon={Database}
            label={t('addressMgmt.dmpConsole')}
            onClick={() => {
              setMenuOpen(false);
              // `|| null`: a no-address ticket's reference may carry jvid 0 — treat it as "no jvid".
              openDmpConsole(context?.jvid || null, {
                street: context?.streetName ?? null,
                number: context?.houseNumber ?? null,
                ext: context?.houseNumberExtension ?? null,
                postal: context?.postalCode ?? null,
              }, context?.ticketId ? {
                ticketId: context.ticketId,
                ticketNumber: context.ticketNumber ?? '',
                // "Link this jvid" only makes sense while the ticket has none.
                linkable: !context?.jvid,
              } : undefined);
            }}
          />
        </div>
      )}

      <Sheet open={mode !== null} onOpenChange={(o) => { if (!o) setMode(null); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-y-auto p-6"
          // Don't drop the cursor into the first field (municipality) when the sheet opens — the operator
          // shouldn't have to clear an auto-focused combobox before starting where they want.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {mode === 'create' && <CreateAddressForm context={formContext} onCreated={() => { setMode(null); onMutated?.(); }} />}
          {mode === 'addBox' && <AddBoxForm context={formContext} onCreated={() => { setMode(null); onMutated?.(); }} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick }: { icon: typeof MapPin; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-brand-rose/[0.06] hover:text-slate-900"
    >
      <Icon className="w-4 h-4 text-brand-rose" />
      {label}
    </button>
  );
}

// "Add a box": add one or more units (bus_numbers) to the building the open ticket points at. The next
// bus_numbers are SUGGESTED server-side from the building's existing pattern (editable); duplicates (normalized)
// are flagged and the submit is blocked. Writes new public.address rows to the LOCAL copy (server-guarded).
export function AddBoxForm({ context, onCreated }: { context?: AddressMgmtContext; onCreated: () => void }) {
  const confirmDmpWrite = useDmpWriteConfirm();
  const { t } = useTranslation('tickets');
  // Seul un admin peut passer outre une capacité nulle. Le masquage ici n'est qu'un confort : la règle
  // qui compte est celle du serveur (addAddressBoxes), qui refuse un non-admin quoi qu'affiche l'écran.
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState<BuildingBoxInfo | null>(null);
  const [existing, setExisting] = useState<{ busNumber: string | null; mappedBusNumber?: string | null }[]>([]);
  // Capacité étage ② : la modale de passage outre et son motif (obligatoire pour continuer).
  const [askOverride, setAskOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  // QDX (C0.1) : façade ou arrière, déclaré par le relecteur. Rien par défaut : un choix pré-coché serait un
  // arbitrage que personne n'a fait.
  const [unitPosition, setUnitPosition] = useState<UnitPosition | null>(null);
  // C0.5 — ce que le SERVEUR a répondu en refusant l'ajout : son calcul (capacité nulle) et, pour un QDX, le fait
  // qu'il lui faut la position. Il sert là où l'écran n'avait rien à calculer (la file MDU ne passe pas d'unités),
  // et quand le serveur voit autre chose que l'écran (un voisin a pris la dernière fibre depuis l'ouverture) : dans
  // les deux cas on DEMANDE ce qui manque, avec les chiffres du serveur, au lieu d'afficher son refus tel quel.
  const [serverCapacity, setServerCapacity] = useState<CapacityResult | null>(null);
  const [serverNeedsPosition, setServerNeedsPosition] = useState(false);
  // La réponse déjà donnée à la question PST bis pour CET ajout (voir `confirmOverride`).
  const lastPstBis = useRef<boolean | undefined>(undefined);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // A belmap building can group SEVERAL postal addresses (Borlée 3 + 5, case UT-2026-000401): the
  // reviewer must pick which one the boxes go under. Default = the TICKET's address, never the
  // building's arbitrary first row. Boxes/suggestions/duplicate-checks are scoped to the variant.
  const [variants, setVariants] = useState<BuildingAddressVariant[]>([]);
  const [variantKey, setVariantKey] = useState<string | null>(null);
  const variant = variants.find((v) => v.key === variantKey) ?? null;
  const [boxes, setBoxes] = useState<string[]>(['']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // N29.1 : la question PST bis, en attente de réponse — elle garde le passage outre déjà accordé, pour ne pas le
  // redemander une fois la question répondue.
  const [pstBisAsk, setPstBisAsk] = useState<{ capacityOverride?: NonNullable<AddBoxesRequest['capacityOverride']> } | null>(null);

  const buildingId = context?.belmapBuildingId != null ? Number(context.belmapBuildingId) : null;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchBuildingBoxes({ buildingId, jvid: context?.jvid ?? null, count: 8 })
      .then((r) => {
        if (!alive) return;
        setBuilding(r.building);
        const vs = r.addresses ?? [];
        setVariants(vs);
        const def = vs.find((v) => v.isTicketAddress) ?? vs[0] ?? null;
        setVariantKey(def?.key ?? null);
        setExisting(def ? def.boxes : r.existingBoxes);
        const sugg = def ? def.suggestions : r.suggestions;
        setSuggestions(sugg);
        setBoxes(sugg.length ? [sugg[0]] : ['']);
      })
      .catch(() => { if (alive) setError(t('addressMgmt.box.loadError')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, context?.jvid]);

  function selectVariant(key: string) {
    const v = variants.find((x) => x.key === key);
    if (!v) return;
    setVariantKey(key);
    setExisting(v.boxes);
    setSuggestions(v.suggestions);
    setBoxes(v.suggestions.length ? [v.suggestions[0]] : ['']);
    setUnitPosition(null); // une autre adresse : la position déclarée ne la concerne pas
    setServerCapacity(null); // …ni ce que le serveur a répondu pour la précédente
    setServerNeedsPosition(false);
    lastPstBis.current = undefined;
    setError(null);
  }

  // "Street 5" — the ext is skipped when it just repeats the number (DMP convention ext = full number).
  const variantLabel = (v: BuildingAddressVariant) => [
    v.streetName,
    [v.houseNumber, v.houseNumberExtension && v.houseNumberExtension !== v.houseNumber ? v.houseNumberExtension : null]
      .filter(Boolean).join(' '),
  ].filter(Boolean).join(' ');

  // A box counts as taken under BOTH names: the DMP bus number and the operator's rename from
  // address_mapping_table (user case 2026-08-04 — DMP "1"/"2" are Proximus "03"/"04"). Without the
  // second one the client waved "03" through and only the server refused it.
  const existingNorm = useMemo(
    () => new Set(existing.flatMap((b) => [
      normalizeBusNumber(b.busNumber),
      ...(b.mappedBusNumber ? [normalizeBusNumber(b.mappedBusNumber)] : []),
    ])),
    [existing],
  );

  const setBox = (i: number, val: string) => setBoxes((b) => b.map((x, j) => (j === i ? val : x)));
  const removeBox = (i: number) => setBoxes((b) => (b.length > 1 ? b.filter((_, j) => j !== i) : b));
  // "Add another box" picks the next suggestion not already used (in an input or existing), else a blank.
  const addBox = () => setBoxes((b) => {
    const used = new Set([...b, ...existing.flatMap((e) => [e.busNumber, e.mappedBusNumber ?? null])]
      .map(normalizeBusNumber));
    const next = suggestions.find((s) => !used.has(normalizeBusNumber(s))) ?? '';
    return [...b, next];
  });

  // Per-input duplicate flag: against existing boxes + earlier inputs (normalized).
  const dupFlags = boxes.map((box, i) => {
    if (box.trim() === '') return false;
    const n = normalizeBusNumber(box);
    if (existingNorm.has(n)) return true;
    return boxes.slice(0, i).some((o) => o.trim() !== '' && normalizeBusNumber(o) === n);
  });

  const validBoxes = boxes.map((b) => b.trim()).filter((b) => b !== '');
  const hasDup = dupFlags.some(Boolean);
  const canSubmit = !!building && validBoxes.length > 0 && !hasDup && !submitting;

  const ctxLine = variant
    ? [variantLabel(variant), variant.postalCode, variant.city ?? variant.municipality].filter(Boolean).join(' · ')
    : [
        building?.streetName ?? context?.streetName,
        building ? [building.houseNumber, building.houseNumberExtension].filter(Boolean).join(' ') : null,
        building?.postalCode ?? context?.postalCode,
        building?.city ?? context?.city ?? context?.municipality,
      ].filter(Boolean).join(' · ');

  // ── Capacité, étage ② : l'avertissement AU MOMENT DU GESTE ────────────────────────────────────
  // Le bloc de la fenêtre de validation dit la capacité à l'OUVERTURE du dossier ; l'erreur, elle, se
  // produit à la clôture (03-09 : « c'est dans les 20 %, je clôture » → le ticket revient une semaine
  // après). On recalcule donc ici, avec le nombre de boîtes RÉELLEMENT saisies, et on demande un motif
  // quand ça ne passe pas — motif écrit dans le journal d'audit. Quand il ne reste RIEN, l'ajout est
  // refusé à un non-admin (N3, 10-09 ; et depuis C0.1 : boîtier COCON plein, poteau sans client
  // connecté, QDX à l'arrière).
  // ⚠️ LA CAPACITÉ SUIT LA VARIANTE, comme `existing`, `suggestions` et la garde d'unicité. Elle ne le
  // faisait pas : elle évaluait le bâtiment entier alors que les boîtes se créent sur UNE adresse — le
  // défaut vu en production le 04-09 (jvid 11815065, un poteau et une façade box sous le même
  // building_id). L'appariement se fait par APPARTENANCE DE JVID et jamais par libellé : le repli
  // instantané écrit `houseNumber: null`, et les libellés cessent alors de coïncider.
  const pageApartments = context?.capacityApartments;
  const scopedApartments = !pageApartments ? []
    : variant ? pageApartments.filter((a) => variant.boxes.some((b) => b.jvid === a.jvid))
      : pageApartments;
  // ── C0.5 ② (08-10) — LA CONSOLE DMP. Elle n'a pas les appartements d'un ticket, et n'affichait donc rien : ni
  // capacité, ni façade / arrière, ni demande de motif — la seule écriture contraire relevée par l'audit du 05-10
  // vient de là. Ses unités sont les boîtes de l'adresse choisie, c'est-à-dire les lignes mêmes que la garde du
  // serveur relira à l'écriture ; COCON est lu ICI, puisqu'aucune page ne l'a lu pour elle.
  // Quand la page passe ses appartements (fenêtre de validation), RIEN ne change. La file MDU ne passe ni les uns
  // ni `viaConsole` : son formulaire reste tel quel, et ne demande que ce que le serveur exige (`serverCapacity`).
  const consoleUnits: CapacityUnit[] = !pageApartments && context?.viaConsole && variant
    ? variant.boxes.map((b) => ({
      jvid: b.jvid,
      quadrant: b.quadrant ?? null,
      deliveryStatus: b.deliveryStatus ?? null,
      scopelistV2: b.scopelistV2 ?? null,
      scopelistV15: b.scopelistV15 ?? null,
      outOfScopeAt: b.outOfScopeAt ?? null,
      streetName: variant.streetName,
      houseNumber: variant.houseNumber,
      houseNumberExtension: variant.houseNumberExtension,
    }))
    : [];
  const consoleCocon = useFacadeCocon(capacityNeedsCocon(consoleUnits) ? consoleUnits.map((u) => u.jvid as number) : []);
  // Filtre vide → on n'avertit PAS. Un avertissement calculé sur le mauvais périmètre use la garde plus
  // vite qu'une garde absente.
  const capacityUnits = pageApartments ? capacityUnitsFromApartments(scopedApartments) : consoleUnits;
  // QD / QDX : la place du boîtier vient de la lecture COCON de la PAGE (ou de la console), résumée pour CETTE adresse.
  const { facade: capacityFacade, pending: coconPending } = capacityUnits.length
    ? facadeInputFor(capacityUnits, pageApartments ? context?.capacityCocon : consoleCocon)
    : { facade: undefined, pending: false };
  // QDX : le serveur exige la position ; l'écran la demande à tout le monde, parce qu'elle change le calcul
  // affiché et qu'elle est tracée dans l'audit. `serverNeedsPosition` : le serveur l'a réclamée sur un écran
  // qui n'avait pas de quoi le savoir.
  const needsPosition = (capacityUnits.length > 0 && capacityNeedsPosition(capacityUnits)) || serverNeedsPosition;
  const localCapacity = capacityUnits.length
    ? computeCapacity({
      units: capacityUnits,
      requested: Math.max(1, validBoxes.length),
      facade: capacityFacade,
      unitPosition: needsPosition ? unitPosition : null,
    })
    : null;
  // Le serveur fait foi au moment d'écrire : son calcul, quand il a refusé, remplace celui de l'écran jusqu'à ce
  // que l'adresse ou la position change.
  const capacity = serverCapacity ?? localCapacity;
  // Tant que COCON n'a pas répondu, ou que la position QDX n'est pas donnée, on n'envoie rien : un clic
  // rapide passerait sinon sur un calcul qui n'est pas encore le bon.
  const capacityReady = !coconPending && (!needsPosition || unitPosition != null);
  const capacityWarns = capacity != null && capacity.verdict !== 'ok';
  // ── Capacité nulle : BLOCAGE (décision user 2026-09-10, priorité n°1 de la réunion du jour) ──────
  // ⚠️ DEUX PRÉDICATS, PAS UN SEUIL DÉPLACÉ. `capacityWarns` ci-dessus couvre trois situations que la
  // décision ne traite PAS de la même façon, et déplacer sa borne les aurait confondues :
  //  · `available === 0` → bloqué. **24 adresses sur 216 mesurées en prod le 10-09**, dont 15 déjà en
  //    dépassement (computeCapacity ramène leur reste à 0) — d'où la dérogation admin, sans laquelle
  //    elles deviendraient incorrigibles depuis l'app.
  //  · `available === null` (« on ne sait pas ») → **51 adresses sur 216 : surtout PAS bloquées.** Le
  //    bloc de capacité a un ton gris exprès, « le pire résultat serait un “je ne sais pas” qui se lit
  //    “c'est bon” » ; le bloquer serait le symétrique, aussi faux, et fermerait un quart des dossiers.
  //  · `verdict === 'build'` avec `available > 0` → il reste de la place, moins que demandé. Inchangé :
  //    refuser 3 boîtes quand il en reste 2 n'est pas « capacité nulle ».
  // Le serveur applique la MÊME règle (addAddressBoxes) : ici on évite le geste inutile, là-bas on
  // garantit le refus.
  const capacityBlocks = capacity != null && capacity.available === 0;
  const blockedForMe = capacityBlocks && !isAdmin;

  function requestSubmit() {
    if (capacityWarns) { setOverrideReason(''); setAskOverride(true); return; }
    void handleSubmit();
  }

  function confirmOverride() {
    if (!capacity || !overrideReason.trim()) return;
    setAskOverride(false);
    // `lastPstBis` : quand la fenêtre du motif s'ouvre APRÈS un refus du serveur, la question PST bis a déjà reçu sa
    // réponse — on ne la repose pas.
    void handleSubmit({
      reason: overrideReason.trim(),
      verdict: capacity.verdict === 'build' ? 'build' : 'unknown',
      available: capacity.available,
      requested: capacity.requested ?? validBoxes.length,
      quadrant: capacity.quadrant,
      rule: capacity.rule,
      unitPosition: needsPosition ? unitPosition : null,
      cocon: capacity.facade
        ? {
          boxes: capacity.facade.boxes.length,
          connected: capacity.facade.connected,
          planned: capacity.facade.planned.length,
          available: capacity.facade.available,
        }
        : null,
    }, lastPstBis.current);
  }

  async function handleSubmit(capacityOverride?: NonNullable<AddBoxesRequest['capacityOverride']>, pstBis?: boolean) {
    if (!building) return;
    setError(null);
    if (pstBis !== undefined) lastPstBis.current = pstBis;
    // N29.1 — « Build doit-il avoir ces boîtes ? », APRÈS l'avertissement de capacité, jamais par-dessus. Sa réponse
    // relance cette même fonction ; « Revenir au formulaire » n'écrit rien.
    const offered = pstBisOffered(context);
    if (offered && pstBis === undefined) {
      setPstBisAsk({ capacityOverride });
      return;
    }
    if (!confirmDmpWrite('ajouter les box dans le DMP')) return;
    setSubmitting(true);
    try {
      const res = await addBoxes({
        buildingId: building.belmapBuildingId, boxes: validBoxes, inScope: context?.inScope,
        ticketNumber: context?.ticketNumber ?? null,
        // N29.1 : le serveur lit sur CE ticket si Build a répondu (la console, elle, n'en a pas).
        ...(context?.viaConsole ? {} : { ticketId: context?.ticketId ?? null }),
        ...(offered ? { pstBis: pstBis === true } : {}),
        // Pin the postal address the boxes are created under (never the building's first row).
        templateJvid: variant?.templateJvid ?? context?.jvid ?? null,
        ...(needsPosition && unitPosition ? { unitPosition } : {}),
        ...(capacityOverride ? { capacityOverride } : {}),
      }, context?.viaConsole);
      const { created, skipped } = res.data;
      toast.success(t('addressMgmt.box.done', { created: created.length, skipped: skipped.length }));
      if (offered && pstBis === true && created.length > 0) toast.success(t('addressMgmt.pstBis.queued'));
      onCreated();
    } catch (err: unknown) {
      // C0.5 — la garde du serveur a refusé. Son refus dit QUOI : on demande ce qui manque (le motif d'un admin, la
      // position d'une unité QDX) ou on montre pourquoi c'est bloqué, avec SON calcul — plutôt que sa phrase.
      const body = err instanceof ApiError ? err.body : undefined;
      const refused = body && typeof body.capacity === 'object' && body.capacity !== null
        ? (body.capacity as CapacityResult) : null;
      if (body?.code === 'CAPACITY_POSITION_REQUIRED') {
        setServerNeedsPosition(true);
        setError(t('addressMgmt.box.positionRequired'));
      } else if (refused && (body?.code === 'CAPACITY_REASON_REQUIRED' || body?.code === 'CAPACITY_BLOCKED')) {
        setServerCapacity(refused);
        // Un admin : la fenêtre du motif s'ouvre, chiffres du serveur sous les yeux. Un validateur : le bouton
        // laisse place à la raison du blocage (`blockedForMe`).
        if (body.code === 'CAPACITY_REASON_REQUIRED') { setOverrideReason(''); setAskOverride(true); }
      } else {
        setError((err as Error)?.message || t('addressMgmt.box.error'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <SheetTitle className="flex items-center gap-2">
        <Box className="w-5 h-5 text-brand-rose" /> {t('addressMgmt.addBoxTitle')}
      </SheetTitle>
      <SheetDescription className="mt-1">{t('addressMgmt.addBoxSubtitle')}</SheetDescription>

      {/* Building these boxes attach to (from the open ticket) */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
          <Building2 className="w-3.5 h-3.5" /> {t('addressMgmt.building')}
        </div>
        <div className="mt-1 text-sm font-semibold text-slate-800">{ctxLine || '—'}</div>
        {(building?.belmapBuildingId ?? context?.belmapBuildingId) != null && (
          <div className="text-xs text-slate-400">belmap_building_id · {building?.belmapBuildingId ?? context?.belmapBuildingId}</div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : !building ? (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
          {t('addressMgmt.box.empty')}
        </p>
      ) : (
        <>
          {/* Several postal addresses under this building id → the reviewer PICKS the target
              (default: the ticket's address). Case UT-2026-000401: Borlée 3 vs 5. */}
          {variants.length > 1 && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
              <label className="block text-xs font-semibold text-amber-800">
                {t('addressMgmt.box.targetAddress', { count: variants.length })}
              </label>
              <select
                value={variantKey ?? ''}
                onChange={(e) => selectVariant(e.target.value)}
                className="mt-1.5 h-9 w-full cursor-pointer rounded-lg border border-amber-300 bg-white px-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-rose/40"
              >
                {variants.map((v) => (
                  <option key={v.key} value={v.key}>
                    {variantLabel(v)} — {t('addressMgmt.box.unitCount', { count: v.unitCount })}
                    {v.isTicketAddress ? ` ${t('addressMgmt.box.ticketAddressSuffix')}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Existing boxes (read-only) */}
          <div className="mt-4">
            <span className="block text-xs font-medium text-slate-500">
              {t('addressMgmt.box.existing', { count: existing.length })}
            </span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {existing.length === 0 ? (
                <span className="text-xs text-slate-400">{t('addressMgmt.box.none')}</span>
              ) : (
                existing.map((b, i) => {
                  // Show the number the OPERATOR uses when it differs from the DMP one, with both in
                  // the tooltip — otherwise the reviewer reads "1" here and "03" on the ticket.
                  const mapped = b.mappedBusNumber?.trim() || null;
                  const dmp = b.busNumber?.trim() || null;
                  const renamed = mapped != null && mapped !== dmp;
                  return (
                    <span
                      key={i}
                      title={renamed ? t('addressMgmt.box.mappedHint', { mapped, dmp: dmp || '—' }) : undefined}
                      className={cn(
                        'rounded-md px-2 py-0.5 text-xs font-medium',
                        renamed ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600',
                      )}
                    >
                      {mapped ?? dmp ?? '—'}
                    </span>
                  );
                })
              )}
            </div>
          </div>

          {/* Boxes to add (prefilled from the suggestion, editable) */}
          <div className="mt-4 space-y-2">
            <span className="block text-xs font-medium text-slate-500">{t('addressMgmt.boxesLabel')}</span>
            {boxes.map((b, i) => (
              <div key={i}>
                <div className="flex items-center gap-2">
                  <Input
                    value={b}
                    onChange={(e) => { setBox(i, e.target.value); setError(null); }}
                    placeholder={t('addressMgmt.boxPlaceholder')}
                    className={cn(dupFlags[i] && 'border-red-300')}
                  />
                  <button
                    type="button"
                    onClick={() => removeBox(i)}
                    disabled={boxes.length === 1}
                    className="shrink-0 rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
                    aria-label={t('addressMgmt.removeBox')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {dupFlags[i] && <p className="mt-0.5 text-[10px] text-red-500">{t('addressMgmt.box.duplicate')}</p>}
              </div>
            ))}
            <button
              type="button"
              onClick={addBox}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-rose hover:underline"
            >
              <Plus className="w-3.5 h-3.5" /> {t('addressMgmt.addAnotherBox')}
            </button>
            <p className="text-[10px] text-slate-400">{t('addressMgmt.box.suggestionHint')}</p>
          </div>

          {/* C0.5 ② — la console n'a ni tableau ni pastille : le verdict s'affiche ICI, tel que la fenêtre de
              validation le montre dans sa modale (même composant, même calcul). */}
          {!pageApartments && context?.viaConsole && capacity && (
            <div className="mt-4">
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-500">
                <Gauge className="h-3.5 w-3.5" /> {t('validation.capacity.title')}
              </p>
              <CapacitySummary result={capacity} pending={coconPending} />
            </div>
          )}
          {/* C0.5 ④ (décision du user du 07-10 : avertir sans bloquer) — aucune unité de l'adresse n'est dans la
              build list : y ajouter une boîte, c'est créer là où personne n'a construit. Jamais dans la file MDU
              (`inScope`) : y créer est le geste normal. */}
          {capacity?.reserves.includes('noBuildList') && !context?.inScope && (
            <div role="note" className="mt-3 rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2.5">
              <p className="flex items-center gap-1.5 text-sm font-bold text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {t('addressMgmt.unbuilt.title')}
              </p>
              <p className="mt-1 text-xs text-amber-900">{t('addressMgmt.unbuilt.addBody')}</p>
            </div>
          )}
          {/* QDX (C0.1) : l'app ne peut pas savoir où est l'unité — le relecteur le dit, et c'est tracé. */}
          {needsPosition && (
            <fieldset className="mt-3 rounded-lg border border-slate-200 p-2.5">
              <legend className="px-1 text-xs font-semibold text-slate-700">{t('addressMgmt.box.positionLabel')}</legend>
              <div className="flex flex-wrap gap-4">
                {(['FACADE', 'REAR'] as const).map((p) => (
                  <label key={p} className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                    <input
                      type="radio"
                      name="unit-position"
                      checked={unitPosition === p}
                      // Une autre position change le calcul : ce que le serveur avait répondu ne vaut plus.
                      onChange={() => { setUnitPosition(p); setServerCapacity(null); setError(null); }}
                    />
                    {t(p === 'FACADE' ? 'addressMgmt.box.positionFacade' : 'addressMgmt.box.positionRear')}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">{t('addressMgmt.box.positionHint')}</p>
            </fieldset>
          )}
          {coconPending && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('addressMgmt.box.coconLoading')}
            </p>
          )}
          {error && <p className="mt-3 text-xs font-medium text-red-600" role="alert">{error}</p>}
          {/* Always-on recap of WHERE the boxes land — the zero-cost safety net. */}
          {ctxLine && (
            <p className="mt-3 text-xs text-slate-600">
              {t('addressMgmt.box.recap')}{' '}
              <span className="font-semibold text-slate-800">{ctxLine}</span>
            </p>
          )}
          {/* Capacité nulle et l'utilisateur n'est pas admin : le bouton disparaît, et il est REMPLACÉ
              par la raison. Un bouton qui s'évapore sans un mot laisse croire à une panne — et c'est
              justement le reproche fait à l'app en réunion : dire ce qui bloque, pas seulement bloquer.
              Le renvoi vers Build est du TEXTE : le routage pré-rempli (étage ③, code 2110) attend HC. */}
          {blockedForMe ? (
            <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" role="alert">
              <span className="font-semibold">{t('addressMgmt.box.capacityBlockedTitle')}</span>{' '}
              {/* La raison dépend de la règle : dire « plafond 0 » d'un poteau sans client connecté ou d'une
                  unité à l'arrière serait faux — ces deux-là ne sont pas pleins, ils demandent une construction. */}
              {capacity?.rule === 'poleBuild'
                ? t('addressMgmt.box.capacityBlockedPoleBuild')
                : capacity?.rule === 'qdxRear'
                  ? t('addressMgmt.box.capacityBlockedQdxRear')
                  : capacity?.rule === 'facadeCocon' && capacity.facade
                    ? t('addressMgmt.box.capacityBlockedCocon', {
                      connected: capacity.facade.connected,
                      ports: capacity.facade.ports,
                      planned: capacity.facade.planned.length,
                    })
                    : t('addressMgmt.box.capacityBlockedBody', {
                      built: capacity?.built ?? 0,
                      added: capacity?.added ?? 0,
                      cap: capacity?.cap ?? 0,
                    })}
            </p>
          ) : (
            <>
              {/* La capacité ne teinte PAS le bouton : le teindre en rouge se lirait comme un blocage, et
                  hors capacité nulle rien n'est bloqué. L'avertissement arrive au clic, calcul sous les yeux. */}
              <Button onClick={requestSubmit} disabled={!canSubmit || !capacityReady} className="mt-2 w-full">
                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                {submitting ? t('addressMgmt.box.submitting') : t('addressMgmt.box.submit')}
              </Button>
              {/* L'admin garde le geste, mais on lui dit d'emblée qu'il déroge : la modale suivante lui
                  demandera un motif, et ce motif est la seule trace qui restera de l'arbitrage. */}
              {capacityBlocks && (
                <p className="mt-2 text-xs text-amber-700">{t('addressMgmt.box.capacityAdminOverride')}</p>
              )}
            </>
          )}
        </>
      )}

      {/* Passage outre la capacité. Rendu DANS le formulaire (donc dans le Sheet) : ConfirmDialog ne
          se portalise pas, ses clics restent « dedans » pour Radix. Le motif est obligatoire — c'est
          ce qui distingue un arbitrage assumé d'un clic distrait, et c'est lui qui part dans l'audit. */}
      {capacity && (
        <ConfirmDialog
          open={askOverride}
          tone="amber"
          title={t('addressMgmt.box.capacityTitle')}
          message={(
            <div className="space-y-2 text-left">
              <p className="text-sm text-slate-600">
                {capacity.verdict === 'build'
                  ? t('addressMgmt.box.capacityBuild', { available: capacity.available ?? 0, requested: validBoxes.length })
                  : t('addressMgmt.box.capacityUnknown')}
              </p>
              <p className="text-xs text-slate-500">
                {capacity.facade
                  ? t('addressMgmt.box.capacityCalcCocon', {
                    quadrant: capacity.quadrant ?? '—',
                    boxes: capacity.facade.boxes.length,
                    connected: capacity.facade.connected,
                    planned: capacity.facade.planned.length,
                  })
                  : t('addressMgmt.box.capacityCalc', {
                    built: capacity.built,
                    added: capacity.added,
                    quadrant: capacity.quadrant ?? '—',
                  })}
              </p>
              <textarea
                className="w-full rounded-lg border border-slate-200 p-2 text-sm"
                rows={3}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder={t('addressMgmt.box.capacityReasonPlaceholder')}
              />
              <p className="text-[11px] text-slate-400">{t('addressMgmt.box.capacityTraced')}</p>
            </div>
          )}
          confirmLabel={t('addressMgmt.box.capacityConfirm')}
          cancelLabel={t('addressMgmt.box.capacityCancel')}
          busy={submitting}
          confirmDisabled={!overrideReason.trim()}
          onCancel={() => setAskOverride(false)}
          onConfirm={confirmOverride}
        />
      )}

      {/* N29.1 — la dernière question avant d'écrire (voir PstBisDialog). */}
      <PstBisDialog
        open={pstBisAsk != null}
        kind="boxes"
        viaConsole={!!context?.viaConsole}
        refusal={context?.buildDeclineMode}
        busy={submitting}
        onYes={() => { const a = pstBisAsk; setPstBisAsk(null); void handleSubmit(a?.capacityOverride, true); }}
        onNo={() => {
          const a = pstBisAsk; setPstBisAsk(null);
          // N29.4 : sans ticket (console), « Non » crée sans PST bis ; sur un ticket, rien n'est créé, la page prépare le refus.
          if (context?.viaConsole) void handleSubmit(a?.capacityOverride, false);
          else context?.onBuildDeclined?.();
        }}
        onCancel={() => setPstBisAsk(null)}
      />
    </div>
  );
}
